// File: src/utils/revealSocket.js
import { io } from "socket.io-client";
import axios from "axios";

let socket;

/* ───────── auth/user helpers ───────── */
function getCreds() {
  const auth = JSON.parse(localStorage.getItem("tribe_auth") || "{}");
  const token =
    auth?.token ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("refreshToken") ||
    "";
  const userId =
    auth?.uid ||
    auth?.userId ||
    auth?.user?._id ||
    localStorage.getItem("userId") ||
    "";
  return { token, userId };
}
function authHeaders() {
  const { token } = getCreds();
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
}

function joinUserRoomSafe() {
  try {
    const { userId } = getCreds();
    if (userId && socket?.connected) {
      socket.emit("joinUserRoom", String(userId));
      console.log("[RevealSocket] joined user room user_%s", String(userId));
    } else if (!userId) {
      console.warn("[RevealSocket] no userId available to join user room");
    }
  } catch (e) {
    console.warn("[RevealSocket] joinUserRoom failed:", e?.message || e);
  }
}

export function refreshRevealSocketAuth({ reconnectIfNeeded = true } = {}) {
  if (!socket) return;
  const { token } = getCreds();
  socket.auth = { token };
  if (reconnectIfNeeded && !socket.connected) {
    try { socket.connect(); } catch {}
  }
}

/* ───────── ACCESS WATCHDOG (server /access gate) ───────── */
const accessTimers = new Map(); // eventId -> { tid, onDenied, intervalMs }

/** One-shot /access */
export async function checkRevealAccess(eventId) {
  const api = process.env.REACT_APP_API_URL;
  await axios.get(`${api}/reveal-mode/${eventId}/access`, authHeaders());
  return true;
}

/** Start periodic /access polling */
export function startRevealAccessWatch(eventId, { onDenied, intervalMs = 15000 } = {}) {
  stopRevealAccessWatch(eventId); // singleton per event

  const api = process.env.REACT_APP_API_URL;
  let tid;

  const tick = async () => {
    try {
      const { data } = await axios.get(`${api}/reveal-mode/${eventId}/access`, authHeaders());
      const ok = data?.ok ?? true;
      if (!ok) {
        if (typeof onDenied === "function") onDenied(data);
        return; // stop
      }
    } catch (err) {
      const s = err?.response?.status;
      if (s === 401 || s === 403) {
        if (typeof onDenied === "function") onDenied(err?.response?.data);
        return; // stop
      }
      // transient: keep going
    }
    tid = setTimeout(tick, intervalMs);
    accessTimers.set(eventId, { tid, onDenied, intervalMs });
  };

  tick(); // immediate
  accessTimers.set(eventId, { tid, onDenied, intervalMs });
}

/** Stop periodic /access polling */
export function stopRevealAccessWatch(eventId) {
  const rec = accessTimers.get(eventId);
  if (rec?.tid) clearTimeout(rec.tid);
  accessTimers.delete(eventId);
}

/* ───────── LOCATION HEARTBEAT (presence) ─────────
   Keeps User.latitude/longitude fresh on the server.
   - Sends PUT /users/:id/location every ~15s and on movement.
   - Also emits socket 'locationUpdate' for live reactions. */
let locWatchId = null;
let locTimerId = null;
let lastSentAt = 0;
const LOC_INTERVAL_MS = 15000;

export function startLocationHeartbeat() {
  stopLocationHeartbeat(); // ensure singleton
  const { userId } = getCreds();
  if (!userId) return;

  // fast one-shot
  pushOnce();

  // periodic fallback
  locTimerId = setInterval(pushOnce, LOC_INTERVAL_MS);

  // continuous updates
  try {
    locWatchId = navigator.geolocation.watchPosition(
      pos => pushOnce(pos),
      _err => { /* ignore; periodic fallback still runs */ },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
    );
  } catch {}

  async function pushOnce(position) {
    const now = Date.now();
    if (now - lastSentAt < 4000) return; // throttle watch bursts
    lastSentAt = now;

    try {
      if (!position) {
        position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true, timeout: 6000, maximumAge: 0
          });
        });
      }
      const { latitude, longitude } = position.coords || {};
      if (typeof latitude !== "number" || typeof longitude !== "number") return;

      // Persist latest coords for /access
      await axios.put(
        `${process.env.REACT_APP_API_URL}/users/${userId}/location`,
        { latitude, longitude },
        authHeaders()
      );

      // Optional: live emit for instant server reactions (kick/occupancy)
      if (socket?.connected) {
        socket.emit("locationUpdate", { userId, latitude, longitude });
      }
    } catch {
      /* ignore; next tick tries again */
    }
  }
}

export function stopLocationHeartbeat() {
  if (locWatchId != null) {
    try { navigator.geolocation.clearWatch(locWatchId); } catch {}
  }
  locWatchId = null;
  if (locTimerId != null) clearInterval(locTimerId);
  locTimerId = null;
}

/* ───────── SOCKET LIFECYCLE ───────── */
export function getRevealSocket() {
  if (socket?.connected) return socket;

  if (socket && !socket.connected) {
    refreshRevealSocketAuth();
    return socket;
  }

  const { token } = getCreds();

  socket = io(process.env.REACT_APP_API_URL, {
    transports: ["websocket", "polling"],
    withCredentials: true,
    auth: { token },
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 300,
    reconnectionDelayMax: 3000,
    timeout: 8000,
    pingInterval: 12000,
    pingTimeout: 8000,
  });

  socket.on("connect", () => {
    console.log("[RevealSocket] ✅ connected id=", socket.id);
    refreshRevealSocketAuth({ reconnectIfNeeded: false });
    joinUserRoomSafe();

    // Re-check /access for any active watchers on reconnect
    for (const [eventId, rec] of accessTimers.entries()) {
      checkRevealAccess(eventId).catch(() => {
        if (typeof rec?.onDenied === "function") rec.onDenied({ reason: "access_denied" });
      });
    }
  });

  socket.on("reconnect_attempt", () => {
    refreshRevealSocketAuth({ reconnectIfNeeded: false });
  });

  socket.on("disconnect", (reason) => {
    console.log("[RevealSocket] ❌ disconnected:", reason);
  });

  socket.on("connect_error", (err) => {
    console.error("[RevealSocket] 🚨 connect_error:", err?.message || err);
  });

  // Try immediately too (covers rare race)
  joinUserRoomSafe();

  // reflect token/user changes across tabs
  const onStorage = (e) => {
    if (!e) return;
    const keys = ["accessToken", "tribe_auth", "refreshToken", "userId"];
    if (!keys.includes(e.key)) return;
    refreshRevealSocketAuth({ reconnectIfNeeded: false });
    joinUserRoomSafe();
  };
  try { window.addEventListener("storage", onStorage); } catch {}

  return socket;
}
